'use client'
import { motion } from 'framer-motion'
import { useLang } from '@/context/LangContext'
import { t, toLocalDigits } from '@/lib/translations'
import { hostingFrameworks, hostingHub, type HostingPageContent } from '@/data/hostingPages'
import { hostingPathForSlug } from '@/lib/hosting'
import { consoleLoginUrl } from '@/lib/urls'
import { s3AssetUrl } from '@/lib/s3'
import Faq from '@/components/Faq'
import Icon from '@/components/Icon'

interface HostingPageProps {
  page: HostingPageContent
}

const fixLabel = { fa: 'راه‌حل', en: 'Fix', ar: 'الحل' }
const relatedHint = {
  fa: 'راهنمای میزبانی',
  en: 'Hosting guide',
  ar: 'دليل الاستضافة',
}
const metricLabel = { fa: 'معیار', en: 'Metric', ar: 'المعيار' }
const serviceLinks = [
  {
    href: 'database',
    label: { fa: 'دیتابیس مدیریت‌شده', en: 'Managed database', ar: 'قاعدة بيانات مُدارة' },
  },
  {
    href: 'storage',
    label: { fa: 'فضای ذخیره‌سازی S3', en: 'S3 object storage', ar: 'تخزين S3' },
  },
  {
    href: 'docs',
    label: { fa: 'نحوه‌ی محاسبه‌ی صورت‌حساب', en: 'How billing works', ar: 'كيفية حساب الفاتورة' },
  },
]

export default function HostingPage({ page }: HostingPageProps) {
  const lang = useLang()
  const hasTerminal = Boolean(page.hero.terminal)
  const frameworkLabel = (slug: string) =>
    hostingFrameworks.find((f) => f.slug === slug)?.label ?? slug

  return (
    <>
      {/* [tag=hosting-hero] */}
      <section className="relative overflow-hidden pt-28 pb-14 sm:pt-36 sm:pb-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_78%_-10%,rgb(var(--brand-rgb)/0.2),transparent_60%)]" />
          <div className="absolute inset-0 dot-pattern opacity-[0.35]" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#07070d] to-transparent" />
        </div>

        <div className="relative max-w-6xl mx-auto px-8">
          <nav
            aria-label="breadcrumb"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#64748b]"
          >
            <a href={`/${lang}`} className="hover:text-white transition-colors">
              {t(lang, hostingHub.labels.home)}
            </a>
            <span aria-hidden="true">/</span>
            <a href={`/${lang}/hosting`} className="hover:text-white transition-colors">
              {t(lang, hostingHub.labels.hub)}
            </a>
            <span aria-hidden="true">/</span>
            <span className="text-[#94a3b8]">{page.name}</span>
          </nav>

          <div className="mt-9 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className={hasTerminal ? 'lg:col-span-6' : 'lg:col-span-8'}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1.5 text-xs text-[#94a3b8]">
                <img
                  src={s3AssetUrl(page.slug)}
                  alt=""
                  aria-hidden="true"
                  className="h-4 w-4 object-contain"
                  loading="eager"
                />
                {t(lang, page.hero.eyebrow)}
              </span>

              <h1
                className="mt-6 text-[2.05rem] sm:text-5xl lg:text-[3.3rem] font-bold tracking-tight text-white"
                // Unitless line-height: Tailwind's `lg:text-[3.3rem]` sets a rem
                // line box that is too tight for Persian ascenders.
                style={{ lineHeight: 1.22 }}
              >
                <span className="block">{t(lang, page.hero.title)}</span>
                <span className="mt-1 block text-[var(--brand-300)]">
                  {t(lang, page.hero.titleAccent)}
                </span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-[#94a3b8] leading-relaxed max-w-xl">
                {t(lang, page.hero.lead)}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <a
                  href={consoleLoginUrl}
                  className="text-center px-6 py-3 rounded-lg bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] text-white text-sm font-medium shadow-[0_18px_40px_-20px_rgb(var(--brand-rgb)/0.9)] hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200"
                >
                  {t(lang, page.hero.primaryCta)}
                </a>
                <a
                  href={`/${lang}/pricing/calculator`}
                  className="text-center px-6 py-3 rounded-lg border border-white/[0.1] text-[#94a3b8] text-sm font-medium hover:text-white hover:border-white/[0.25] transition-all duration-200"
                >
                  {t(lang, page.hero.secondaryCta)}
                </a>
              </div>

              <ul className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2">
                {page.hero.trust.map((item, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-[#64748b]">
                    <Icon
                      name="check"
                      className="w-3.5 h-3.5 shrink-0 text-[var(--brand-400)]"
                      strokeWidth={2.5}
                    />
                    {t(lang, item)}
                  </li>
                ))}
              </ul>
            </motion.div>

            {page.hero.terminal && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="lg:col-span-6"
              >
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-6 rounded-[2rem] bg-[radial-gradient(circle_at_50%_45%,rgb(var(--brand-rgb)/0.22),transparent_65%)] blur-2xl"
                  />
                  <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0a0a13] shadow-[0_40px_120px_-50px_rgba(89,131,224,0.65)]">
                    <div dir="ltr" className="flex items-center gap-1.5 border-b border-white/[0.06] px-4 py-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-white/[0.08]" />
                      <span className="h-2.5 w-2.5 rounded-full bg-white/[0.08]" />
                      <span className="h-2.5 w-2.5 rounded-full bg-white/[0.08]" />
                      <span
                        dir="ltr"
                        className="ms-3 truncate font-mono text-[11px] text-[#64748b]"
                      >
                        {page.hero.terminal.path}
                      </span>
                      <span dir="ltr" className="ms-auto shrink-0 font-mono text-[11px] text-[#475569]">
                        {page.hero.terminal.branch}
                      </span>
                    </div>
                    <div
                      dir="ltr"
                      className="px-5 py-5 text-left font-mono text-[12.5px] leading-[1.95]"
                    >
                      {page.hero.terminal.lines.map((line, i) =>
                        line.kind === 'live' ? (
                          <div
                            key={i}
                            className="mt-3 flex items-center gap-2 border-t border-white/[0.06] pt-3"
                          >
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--brand-400)] opacity-60 motion-safe:animate-ping" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--brand-400)]" />
                            </span>
                            <span className="text-[var(--brand-300)]">{line.text}</span>
                          </div>
                        ) : line.kind === 'cmd' ? (
                          <div key={i}>
                            <span className="text-[#475569]">$ </span>
                            <span className="text-white">{line.text}</span>
                          </div>
                        ) : (
                          <div key={i}>
                            <span className="text-[var(--brand-400)]">✓ </span>
                            <span className="text-[#94a3b8]">{line.text}</span>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                  <p className="relative mt-4 text-center text-xs text-[#64748b]">
                    {t(lang, page.hero.terminal.caption)}
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* [tag=hosting-toc] */}
      <div className="relative border-y border-white/[0.06] bg-white/[0.01]">
        <div className="mx-auto flex max-w-6xl items-center gap-2 overflow-x-auto px-8 py-3.5">
          <span className="me-2 shrink-0 text-[11px] uppercase tracking-wide text-[#475569]">
            {t(lang, page.toc.label)}
          </span>
          {page.toc.items.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="shrink-0 rounded-full border border-white/[0.06] px-3 py-1.5 text-xs text-[#94a3b8] hover:border-white/[0.18] hover:text-white transition-colors"
            >
              {t(lang, item.title)}
            </a>
          ))}
        </div>
      </div>

      {/* [tag=hosting-answer] */}
      <section id="answer" className="relative scroll-mt-28 py-14 sm:py-16">
        <div className="relative max-w-4xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.answer.heading)}
          </h2>
          {page.answer.paragraphs.map((paragraph, i) => (
            <p
              key={i}
              className={
                i === 0
                  ? 'mt-5 text-lg text-[#cbd5e1] leading-9 border-s-2 border-[rgb(var(--brand-rgb)/0.4)] ps-5'
                  : 'mt-5 text-lg text-[#cbd5e1] leading-9'
              }
            >
              {t(lang, paragraph)}
            </p>
          ))}
        </div>
      </section>

      {/* [tag=hosting-requirements] */}
      <section id="requirements" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-6xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.requirements.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, page.requirements.intro)}
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {page.requirements.items.map((item, i) => (
              <div
                key={i}
                className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6"
              >
                <div className="flex items-center gap-3">
                  <Icon
                    name="check-circle"
                    className="w-5 h-5 text-[var(--brand-400)] shrink-0"
                    strokeWidth={1.5}
                  />
                  <h3 className="text-base font-semibold text-white">{t(lang, item.title)}</h3>
                </div>
                <p className="mt-3 text-sm text-[#94a3b8] leading-relaxed">{t(lang, item.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* [tag=hosting-comparison] */}
      <section id="comparison" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-5xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.comparison.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, page.comparison.intro)}
          </p>

          <div className="mt-8 overflow-x-auto rounded-xl border border-white/[0.06]">
            <table className="w-full border-collapse text-start">
              <thead>
                <tr className="bg-white/[0.03]">
                  <th className="p-4 text-start text-sm font-semibold text-white">
                    <span className="sr-only">{t(lang, metricLabel)}</span>
                  </th>
                  {page.comparison.columns.map((column, i) => (
                    <th
                      key={i}
                      scope="col"
                      className={`p-4 text-start text-sm font-semibold ${
                        i === page.comparison.columns.length - 1
                          ? 'text-[var(--brand-400)]'
                          : 'text-white'
                      }`}
                    >
                      {t(lang, column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.comparison.rows.map((row, i) => (
                  <tr key={i} className="border-t border-white/[0.06]">
                    <th scope="row" className="p-4 text-start text-sm font-medium text-[#cbd5e1]">
                      {t(lang, row.label)}
                    </th>
                    {row.values.map((value, j) => (
                      <td
                        key={j}
                        className={`p-4 text-sm ${
                          j === row.values.length - 1 ? 'text-[#cbd5e1]' : 'text-[#94a3b8]'
                        }`}
                      >
                        {t(lang, value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-[#64748b] leading-relaxed">
            {t(lang, page.comparison.note)}
          </p>
        </div>
      </section>

      {/* [tag=hosting-deploy] */}
      <section id="deploy" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-5xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.deploy.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, page.deploy.intro)}
          </p>

          <ol className="mt-10 space-y-4">
            {page.deploy.steps.map((step, i) => (
              <li
                key={i}
                className="flex gap-5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-6"
              >
                <span
                  className="shrink-0 w-8 h-8 rounded-full border border-[rgb(var(--brand-rgb)/0.35)] text-[var(--brand-400)] text-sm font-semibold flex items-center justify-center"
                  aria-hidden="true"
                >
                  {toLocalDigits(String(i + 1), lang)}
                </span>
                <div>
                  <h3 className="text-base font-semibold text-white">{t(lang, step.title)}</h3>
                  <p className="mt-2 text-sm text-[#94a3b8] leading-relaxed">
                    {t(lang, step.body)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* [tag=hosting-runtime] */}
      <section id="runtime" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-6xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.runtime.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, page.runtime.intro)}
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {page.runtime.items.map((item, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
                <div className="flex items-center gap-3">
                  <Icon name="container" className="w-5 h-5 text-[var(--brand-400)]" strokeWidth={1.5} />
                  <h3 className="text-base font-semibold text-white">{t(lang, item.title)}</h3>
                </div>
                <p className="mt-3 text-sm text-[#94a3b8] leading-relaxed">{t(lang, item.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* [tag=hosting-pricing] */}
      <section id="pricing" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-4xl mx-auto px-8">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 sm:p-10">
            <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
              {t(lang, page.pricing.heading)}
            </h2>
            <p className="mt-4 text-[#94a3b8] leading-relaxed">{t(lang, page.pricing.body)}</p>

            <ul className="mt-6 space-y-3">
              {page.pricing.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#cbd5e1]">
                  <Icon
                    name="check"
                    className="w-4 h-4 mt-0.5 shrink-0 text-[var(--brand-400)]"
                    strokeWidth={2}
                  />
                  <span>{t(lang, bullet)}</span>
                </li>
              ))}
            </ul>

            <a
              href={`/${lang}/pricing/calculator`}
              className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[var(--brand-400)] hover:text-[var(--brand-300)] transition-colors"
            >
              {t(lang, page.pricing.link)}
              <Icon name="arrow-right" className="w-4 h-4 rtl:rotate-180" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* [tag=hosting-migration] */}
      <section id="migration" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-5xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.migration.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, page.migration.intro)}
          </p>

          <ol className="mt-8 space-y-3">
            {page.migration.steps.map((step, i) => (
              <li key={i} className="flex gap-4 border-t border-white/[0.06] pt-4">
                <span className="text-sm text-[var(--brand-400)] font-semibold shrink-0" aria-hidden="true">
                  {toLocalDigits(String(i + 1), lang)}
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-white">{t(lang, step.title)}</h3>
                  <p className="mt-1 text-sm text-[#94a3b8] leading-relaxed">{t(lang, step.body)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* [tag=hosting-troubleshooting] */}
      <section id="troubleshooting" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-5xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">
            {t(lang, page.troubleshooting.heading)}
          </h2>
          <p className="mt-4 text-[#94a3b8] leading-relaxed max-w-2xl">
            {t(lang, page.troubleshooting.intro)}
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {page.troubleshooting.items.map((item, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6">
                <h3 className="text-base font-semibold text-white">{t(lang, item.problem)}</h3>
                <p className="mt-3 text-xs font-medium uppercase tracking-wide text-[#64748b]">
                  {t(lang, fixLabel)}
                </p>
                <p className="mt-1 text-sm text-[#cbd5e1] leading-relaxed">{t(lang, item.fix)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* [tag=hosting-faq] */}
      <section id="faq" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-4xl mx-auto px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-8">
            {t(lang, page.faq.heading)}
          </h2>
          <Faq
            items={page.faq.items.map((f) => ({
              question: t(lang, f.q),
              answer: t(lang, f.a),
            }))}
          />
        </div>
      </section>

      {/* [tag=hosting-related] */}
      <section id="related" className="relative scroll-mt-28 py-16 sm:py-20">
        <div className="relative max-w-5xl mx-auto px-8">
          <h2 className="text-xl font-semibold text-white tracking-tight mb-6">
            {t(lang, page.related.heading)}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {page.related.slugs.map((slug) => (
              <a
                key={slug}
                href={`/${lang}/${hostingPathForSlug(slug)}`}
                className="group flex items-center justify-between gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 hover:border-white/[0.14] hover:bg-white/[0.04] transition-colors"
              >
                <span>
                  <span className="block text-sm font-medium text-white">
                    {frameworkLabel(slug)}
                  </span>
                  <span className="block text-xs text-[#64748b] mt-1">
                    {t(lang, relatedHint)}
                  </span>
                </span>
                <Icon
                  name="arrow-right"
                  className="w-4 h-4 shrink-0 text-[#3f4a5f] group-hover:text-[var(--brand-400)] rtl:rotate-180 transition-colors"
                  strokeWidth={1.5}
                />
              </a>
            ))}
          </div>

          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/[0.06] pt-6">
            {serviceLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={`/${lang}/${link.href}`}
                  className="text-sm text-[#94a3b8] hover:text-white transition-colors"
                >
                  {t(lang, link.label)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* [tag=hosting-cta] */}
      <section className="relative py-20 sm:py-24">
        <div className="relative max-w-4xl mx-auto px-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 text-center">
          <h2 className="text-2xl font-semibold text-white">{t(lang, page.cta.heading)}</h2>
          <p className="mt-3 text-[#94a3b8] leading-relaxed">{t(lang, page.cta.body)}</p>
          <a
            href={consoleLoginUrl}
            className="mt-7 inline-block px-6 py-3 rounded-lg bg-gradient-to-r from-[var(--brand-600)] to-[var(--brand-500)] text-white text-sm font-medium hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] transition-all duration-200"
          >
            {t(lang, page.cta.button)}
          </a>
        </div>
      </section>
    </>
  )
}
